import React, { useState } from 'react';
import { Badge, Button, Collapse } from 'react-bootstrap';
import { formatDateTime, relativeAge } from '../../../Utils/timezone';
import useBookingAiOverview, {
  OVERVIEW_FAILED,
  OVERVIEW_LOADING,
  OVERVIEW_READY,
  OVERVIEW_STREAMING,
} from '../../../Hooks/useBookingAiOverview';

/**
 * AI Overview — the generated briefing at the top of the booking details
 * modal, in the spirit of Google's AI Overview panel: a visually distinct
 * block above the real content, openly machine-written, with its sources one
 * click away.
 *
 * The opposite gate to `AIExtractPanel`, which is for NEW bookings only. This
 * one needs an existing booking to brief on, so it renders and requests
 * nothing without a booking id (see `useBookingAiOverview`).
 *
 * Five states, none of them dramatic:
 *   loading   — a brief shimmer while we find out whether one is stored.
 *               This is a single Mongo read, not a model call.
 *   streaming — a genuine first generation. The facts and the gaps are
 *               already on screen; the prose grows as the model writes it.
 *   ready     — the briefing, its facts, its gaps, its sources.
 *   failed    — ONE quiet muted line. A non-essential panel never raises an
 *               alarm about itself.
 *   idle      — nothing at all (no booking id, or not enabled).
 *
 * STREAMING IS NEVER DECORATIVE. Text grows here only while it is being
 * generated; a stored briefing goes straight to `ready` and is painted
 * whole. There is no typewriter effect and no timer in this component —
 * fake-typing text that already exists would tell the operator a model was
 * thinking when it was not.
 *
 * The prose is rendered as paragraphs rather than Markdown on purpose:
 * `react-markdown` is ESM-only and CRA's jest does not transform it, so every
 * test that renders this modal would have to mock it (see
 * `__tests__/components/ChatPanel.test.jsx`). The contract promises prose.
 */

// One icon per source kind the contract defines, so a glance says where a
// claim came from. `kind` is the contract's enum; anything unknown still gets
// a neutral icon rather than a hole in the list.
const SOURCE_ICONS = {
  booking: 'fa-file-invoice',
  audit: 'fa-clock-rotate-left',
  email: 'fa-envelope',
  document: 'fa-file-lines',
};

const sourceIcon = (kind) => SOURCE_ICONS[kind] || 'fa-link';

/**
 * How much prose shows before "Show more".
 *
 * The briefing is two or three short paragraphs and the box was too tall to
 * read at a glance above the booking details — which are what the operator
 * actually opened the modal for. Measured in characters rather than lines
 * because a line is a layout question and this has to be decided before
 * layout; ~420 is comfortably the first paragraph and the start of the
 * second, which is where the "do I need to read this?" decision gets made.
 */
export const PROSE_COLLAPSE_CHARS = 420;

/** Prose into paragraphs. Blank lines separate them; stray whitespace does not. */
export function paragraphsOf(summary) {
  return String(summary || '')
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
}

/**
 * Where to cut the prose for the collapsed view.
 *
 * Cuts on a paragraph boundary when one is close enough, and otherwise on
 * the last sentence end before the limit — never mid-word, and never
 * mid-sentence. A briefing truncated mid-clause reads as a broken briefing,
 * and this panel's whole problem is operators not trusting it.
 */
export function collapsedProse(summary, limit = PROSE_COLLAPSE_CHARS) {
  const text = String(summary || '').trim();
  if (text.length <= limit) return { head: text, truncated: false };

  const window = text.slice(0, limit);
  const breaks = [window.lastIndexOf('\n\n'), window.lastIndexOf('. ')]
    .filter((at) => at > limit * 0.4);
  const cut = breaks.length ? Math.max(...breaks) : window.lastIndexOf(' ');
  const head = text.slice(0, cut > 0 ? cut + 1 : limit).trim();
  return { head, truncated: true };
}

/**
 * A value the briefing could not establish. Rendered as a visible "unknown",
 * never as a blank — an empty cell beside a filled label reads as a bug, and
 * "we looked and could not tell" is real information.
 */
function Unknown({ children }) {
  return (
    <span className="ai-overview-unknown" title="The briefing could not establish this">
      {children}
    </span>
  );
}

function Fact({ icon, label, children }) {
  return (
    <div className="ai-overview-fact">
      <span className="ai-overview-fact-label">
        <i className={`fa ${icon} me-1`} aria-hidden="true" />
        {label}
      </span>
      <span className="ai-overview-fact-value">{children}</span>
    </div>
  );
}

/** The placeholder. Sized like the briefing so the modal does not jump. */
function OverviewShimmer() {
  return (
    <div
      className="ai-overview-panel ai-overview-loading"
      role="status"
      aria-busy="true"
      data-testid="ai-overview-shimmer"
    >
      <div className="ai-overview-head">
        <span className="ai-overview-title">
          <i className="fa fa-wand-magic-sparkles me-2" aria-hidden="true" />
          AI overview
        </span>
        <span className="ai-overview-working">Writing a briefing…</span>
      </div>
      <div className="ai-overview-body">
        <div className="ai-shimmer-line w-100" />
        <div className="ai-shimmer-line w-75" />
        <div className="ai-shimmer-line w-100" />
        <div className="ai-shimmer-line w-50" />
      </div>
      <span className="visually-hidden">Generating the AI overview for this booking</span>
    </div>
  );
}

/**
 * The facts strip. Rendered from whichever source has them: the finished
 * briefing, or — during a generation — the `overview.facts` event, which
 * arrives before the first word of prose because these fields are computed
 * in code during fact-gathering rather than asked of the model.
 */
export function OverviewFacts({ facts }) {
  const {
    handled_by: handledBy, partner, operators,
  } = facts || {};
  const operatorList = Array.isArray(operators) ? operators : [];

  return (
    <div className="ai-overview-facts">
      {/* `handled_by: null` is a real answer and shows as one. */}
      <Fact icon="fa-user-check" label="Handled by">
        {handledBy || <Unknown>Not identified</Unknown>}
      </Fact>
      <Fact icon="fa-handshake" label="Partner">
        {partner?.name || <Unknown>None recorded</Unknown>}
      </Fact>
      <Fact icon="fa-users" label="Operators">
        {operatorList.length
          ? operatorList.map((op, i) => (
            <span key={`${op?.id || op?.name}-${op?.role || i}`} className="ai-overview-operator">
              {op?.name || 'Unnamed'}
              {op?.role && <span className="ai-overview-role">{op.role}</span>}
            </span>
          ))
          : <Unknown>None assigned</Unknown>}
      </Fact>
    </div>
  );
}

/**
 * TIME TO PICKUP, computed AT RENDER TIME and never baked into the briefing.
 *
 * Several of the measured bookings travelled the next day and the urgency
 * appeared nowhere — an operator wants "it goes in 36 hours" before almost
 * anything else. But it cannot live in the prose, and that is not a layout
 * decision: a briefing is cached until a FACT in it moves, so "travels in 36
 * hours" would be wrong by the next morning with no fingerprint change to
 * catch it. The pickup time has not moved; only the clock has.
 *
 * So the briefing states the pickup time and this states the distance to it,
 * fresh on every render. It is the one part of the panel allowed to disagree
 * with a cached briefing, because it is the one part that must.
 */
export function timeToPickup(pickupAt, now = Date.now()) {
  const at = Date.parse(pickupAt);
  if (Number.isNaN(at)) return null;

  const minutes = Math.round((at - now) / 60000);
  if (minutes < -60 * 24) return { text: 'already travelled', urgent: false };
  if (minutes < 0) return { text: 'pickup time has passed', urgent: true };
  if (minutes < 60) return { text: `in ${minutes} min`, urgent: true };

  const hours = Math.round(minutes / 60);
  if (hours < 48) return { text: `in ${hours} h`, urgent: true };

  return { text: `in ${Math.round(hours / 24)} days`, urgent: false };
}

export function PickupCountdown({ pickupAt }) {
  const when = timeToPickup(pickupAt);
  if (!when) return null;

  return (
    <span
      className={`ai-overview-countdown${when.urgent ? ' is-urgent' : ''}`}
      data-testid="ai-overview-countdown"
    >
      <i className="fa fa-clock me-1" aria-hidden="true" />
      {when.text}
    </span>
  );
}

/**
 * WHAT NEEDS DOING. First in the panel, and never collapsed.
 *
 * Measured across 18 real briefings: not one said what needed attention.
 * None flagged an unassigned driver, none flagged unpaid. An operator
 * opening a booking wants "this needs a driver" before they want the
 * vehicle category, so this sits above the prose rather than inside it.
 *
 * Authored in code, like the gaps — each entry is a hard condition over the
 * record rather than a judgement, and a model that spotted an unassigned
 * driver four times in five would be worse than useless, because then the
 * absence of the line would mean nothing.
 */
export function OverviewAttention({ attention }) {
  const list = Array.isArray(attention) ? attention.filter(Boolean) : [];
  if (!list.length) return null;

  return (
    <ul className="ai-overview-attention" data-testid="ai-overview-attention">
      {list.map((item) => (
        <li key={item}>
          <i className="fa fa-triangle-exclamation me-2" aria-hidden="true" />
          {item}
        </li>
      ))}
    </ul>
  );
}

/**
 * CONFIRMED ABSENCES — visible, never collapsed.
 *
 * "No message has been sent to this customer" is not a caveat about how we
 * looked; it is a finding, from a source that is reliable for the question,
 * and it is frequently the most actionable line in the panel. A confirmed
 * booking two days out that nobody has contacted is work.
 *
 * It used to sit in "Looked for, not found", between "the search hit its
 * page limit" and "the semantic archive was not consulted" — which filed
 * the one line an operator should act on under a heading that reads as
 * "here is where we admit we did not look". So these are separated out,
 * shown without expanding anything, and styled as information rather than
 * as an apology.
 */
export function OverviewFindings({ findings }) {
  const list = Array.isArray(findings) ? findings.filter(Boolean) : [];
  if (!list.length) return null;

  return (
    <ul className="ai-overview-findings" data-testid="ai-overview-findings">
      {list.map((finding) => (
        <li key={finding}>
          <i className="fa fa-circle-exclamation me-2" aria-hidden="true" />
          {finding}
        </li>
      ))}
    </ul>
  );
}

/**
 * Where the record came from. In the strip, never in the prose.
 *
 * The briefing was spending four of its clauses on this — "created via
 * PH_BOOKING_COM_INTEGRATION by mark@kravinskiy.com", "partner is
 * BOOKING.COM - SYNC API with reference 601580036" — and an operator acts
 * on none of it. These fields are no longer sent to the model at all, so
 * this is the only place they appear, where skipping them is free.
 */
export function OverviewProvenance({ provenance }) {
  const {
    created_via: via,
    partner_reference: partnerRef,
    operator_reference: operatorRef,
  } = provenance || {};
  if (!via && !partnerRef && !operatorRef) return null;

  return (
    <div className="ai-overview-provenance" data-testid="ai-overview-provenance">
      {via && (
        <span className="ai-overview-provenance-item">
          <i className="fa fa-right-to-bracket me-1" aria-hidden="true" />
          {via}
        </span>
      )}
      {partnerRef && (
        <span className="ai-overview-provenance-item">
          Partner ref
          <code className="ms-1">{partnerRef}</code>
        </span>
      )}
      {operatorRef && (
        <span className="ai-overview-provenance-item">
          Operator ref
          <code className="ms-1">{operatorRef}</code>
        </span>
      )}
    </div>
  );
}

/**
 * "Looked for, not found" — the point of the feature.
 *
 * "Some emails will have no detail, don't hallucinate" — so what was looked
 * for and NOT found is stated plainly, in neutral type. This is not a
 * warning; it is the briefing being honest, and hiding it would be the bug.
 *
 * CAVEATS ONLY. These describe how we LOOKED — the search was subject-only,
 * the index is best-effort, retention reaches 365 days. Confirmed absences
 * moved to `OverviewFindings`, which is not collapsed.
 *
 * COLLAPSED BY DEFAULT, AND THAT IS NOT HIDING IT. The list runs to a dozen
 * carefully-worded caveats and it was dominating a panel that sits above the
 * booking details. So the HEADING always renders, with the count in it, as a
 * button — the operator is told how many things were looked for and not
 * found before they decide to read them. What is collapsed is the prose, not
 * the fact of it. There is deliberately no way to dismiss or disable this:
 * it has no close button, it is not behind a preference, and it re-renders
 * collapsed-but-present on every open.
 */
export function OverviewGaps({ gaps }) {
  const [open, setOpen] = useState(false);
  const gapList = Array.isArray(gaps) ? gaps.filter(Boolean) : [];
  if (!gapList.length) return null;

  return (
    <div className="ai-overview-gaps" data-testid="ai-overview-gaps">
      <Button
        variant="link"
        size="sm"
        className="ai-overview-gaps-head"
        aria-expanded={open}
        onClick={() => setOpen((isOpen) => !isOpen)}
        data-testid="ai-overview-gaps-toggle"
      >
        <i className="fa fa-magnifying-glass me-2" aria-hidden="true" />
        Looked for, not found
        <Badge bg="light" text="dark" className="ms-2">{gapList.length}</Badge>
        <i className={`fa ${open ? 'fa-chevron-up' : 'fa-chevron-down'} ms-2`} aria-hidden="true" />
      </Button>
      <Collapse in={open}>
        <div>
          <ul className="ai-overview-gaps-list" data-testid="ai-overview-gaps-list">
            {gapList.map((gap) => <li key={gap}>{gap}</li>)}
          </ul>
        </div>
      </Collapse>
    </div>
  );
}

/** The prose, collapsed past `PROSE_COLLAPSE_CHARS` with a "Show more". */
export function OverviewProse({ summary, streaming = false }) {
  const [expanded, setExpanded] = useState(false);

  // While text is still arriving there is nothing to collapse — the
  // operator is watching it be written, and a "Show more" that appears
  // mid-sentence and then moves would be worse than a tall box.
  if (streaming) {
    return (
      <>
        {paragraphsOf(summary).map((text, i, all) => (
          <p className="ai-overview-para" key={text.slice(0, 60)}>
            {text}
            {i === all.length - 1 && (
              <span className="ai-overview-cursor" aria-hidden="true" />
            )}
          </p>
        ))}
      </>
    );
  }

  const { head, truncated } = collapsedProse(summary);
  const shown = expanded || !truncated ? summary : head;

  return (
    <>
      {paragraphsOf(shown).map((text) => (
        <p className="ai-overview-para" key={text.slice(0, 60)}>{text}</p>
      ))}
      {truncated && (
        <Button
          variant="link"
          size="sm"
          className="ai-overview-more"
          aria-expanded={expanded}
          onClick={() => setExpanded((open) => !open)}
          data-testid="ai-overview-more"
        >
          <i className={`fa ${expanded ? 'fa-chevron-up' : 'fa-chevron-down'} me-2`} aria-hidden="true" />
          {expanded ? 'Show less' : 'Show more'}
        </Button>
      )}
    </>
  );
}

/** The sources footer, with the list one click away. */
export function OverviewSources({ sources, model }) {
  const [open, setOpen] = useState(false);
  const sourceList = Array.isArray(sources) ? sources : [];

  return (
    <>
      <div className="ai-overview-foot">
        {sourceList.length > 0 ? (
          <Button
            variant="link"
            size="sm"
            className="ai-overview-sources-toggle"
            aria-expanded={open}
            onClick={() => setOpen((isOpen) => !isOpen)}
          >
            <i className={`fa ${open ? 'fa-chevron-up' : 'fa-chevron-down'} me-2`} aria-hidden="true" />
            {sourceList.length}
            {sourceList.length === 1 ? ' source' : ' sources'}
          </Button>
        ) : (
          // A paraphrase with nothing behind it is the worst thing this
          // panel can produce, so say so where the sources would be.
          <span className="ai-overview-nosources" data-testid="ai-overview-no-sources">
            <i className="fa fa-circle-info me-2" aria-hidden="true" />
            No sources cited
          </span>
        )}
        {model && (
          <span className="ai-overview-model" title="The model that wrote this briefing">
            {model}
          </span>
        )}
      </div>

      <Collapse in={open}>
        <div>
          <ul className="ai-overview-sources" data-testid="ai-overview-sources">
            {sourceList.map((source, i) => (
              <li key={`${source?.kind}-${source?.ref || source?.label || i}`}>
                <i className={`fa ${sourceIcon(source?.kind)} me-2`} aria-hidden="true" />
                <span className="ai-overview-source-label">{source?.label || source?.ref || 'Untitled'}</span>
                {source?.kind && <span className="ai-overview-source-kind">{source.kind}</span>}
                {source?.ref && <code className="ai-overview-source-ref">{source.ref}</code>}
              </li>
            ))}
          </ul>
        </div>
      </Collapse>
    </>
  );
}

/**
 * The briefing itself, given the whole object. Exported so the chat can
 * render a `brief_booking` card with this same component — the gaps and the
 * sources reach the operator through one piece of code whichever surface
 * asked for them.
 */
export function OverviewBody({ overview, partial = '', streaming = false }) {
  const facts = overview || {};
  const summary = streaming ? partial : overview?.summary;

  return (
    <div className="ai-overview-body">
      <OverviewAttention attention={facts.attention} />
      <OverviewProse summary={summary} streaming={streaming} />
      <OverviewFindings findings={facts.findings} />
      <OverviewFacts facts={facts} />
      <OverviewProvenance provenance={facts.provenance} />
      <OverviewGaps gaps={facts.gaps} />
      <OverviewSources sources={facts.sources} model={facts.model} />
    </div>
  );
}

function AIOverviewPanel({ bookingId, enabled = true, pickupAt = null }) {
  const {
    state, overview, facts, partial,
  } = useBookingAiOverview(bookingId, { enabled });

  if (state === OVERVIEW_LOADING) return <OverviewShimmer />;

  // One line, muted, dismissible by ignoring it. The booking details are
  // untouched and the user came for those.
  if (state === OVERVIEW_FAILED) {
    return (
      <p className="ai-overview-quiet" data-testid="ai-overview-unavailable">
        <i className="fa fa-wand-magic-sparkles me-2" aria-hidden="true" />
        No AI overview for this booking right now.
      </p>
    );
  }

  // A GENUINE first generation, in progress. The facts and the gaps are
  // already known — they were computed in code before the model was called
  // — so they render immediately and the prose fills in beneath them.
  if (state === OVERVIEW_STREAMING) {
    return (
      <section
        className="ai-overview-panel ai-overview-generating"
        data-testid="ai-overview-streaming"
        aria-busy="true"
      >
        <div className="ai-overview-head">
          <span className="ai-overview-title">
            <i className="fa fa-wand-magic-sparkles me-2" aria-hidden="true" />
            AI overview
          </span>
          <Badge className="ai-overview-badge" pill>AI-generated</Badge>
          <PickupCountdown pickupAt={pickupAt} />
          <span className="ai-overview-working" role="status">Writing a briefing…</span>
        </div>
        <div className="ai-overview-body">
          {/* Already known before the model was called, so it is on screen
              before the prose — which is the right way round: what needs
              doing should not wait for a description of the trip. */}
          <OverviewAttention attention={facts?.attention} />
          <OverviewProse summary={partial} streaming />
          <OverviewFindings findings={facts?.findings} />
          <OverviewFacts facts={facts || {}} />
          <OverviewProvenance provenance={facts?.provenance} />
          <OverviewGaps gaps={facts?.gaps} />
          <OverviewSources sources={facts?.sources} />
        </div>
      </section>
    );
  }

  if (state !== OVERVIEW_READY || !overview) return null;

  const { generated_at: generatedAt } = overview;
  const age = relativeAge(generatedAt);

  return (
    <section className="ai-overview-panel" data-testid="ai-overview">
      <div className="ai-overview-head">
        <span className="ai-overview-title">
          <i className="fa fa-wand-magic-sparkles me-2" aria-hidden="true" />
          AI overview
        </span>
        <Badge className="ai-overview-badge" pill>AI-generated</Badge>
        <PickupCountdown pickupAt={pickupAt} />
        {generatedAt && (
          <span className="ai-overview-age" title={`Generated ${formatDateTime(generatedAt)} (Budapest)`}>
            {formatDateTime(generatedAt)}
            {age && ` · ${age}`}
          </span>
        )}
      </div>
      <OverviewBody overview={overview} />
    </section>
  );
}

export default AIOverviewPanel;
